/**
 * browserSignInCards.ts — the ONE place the chat's sign-in cards are told where their sign-in stands
 * (slice D of the browser sign-in program; Miles's ruling R1, 2026-10-01).
 *
 * The browser-lane card (connectApp.ts buildBrowserLaneCardHtml) is html the chat draws as it was stored, so
 * nothing re-renders it when a sign-in moves on. This module paints its one pill from the phase store
 * (browserSignInPhase.ts): every card for the same app, wherever it is in the thread, says the same thing.
 *
 *   - `installBrowserSignInCards(document)` (the sign-in window's host mounts it once, app-wide) paints the
 *     cards on the page at every change, and watches for cards drawn afterwards (a thread opened again, a
 *     card's html replaced, a new card), like the spinners' one clock (markSpinnerPhase.ts):
 *       · while a window is opening, open or left unfinished, EVERY card for the app takes that state:
 *         pressing any of them continues the same sign-in;
 *       · "signed in" goes to the cards that were on the page when it happened, and stays with them when they
 *         are drawn again (they are remembered by their message); a card posted LATER is a fresh ask and
 *         keeps its own words;
 *       · the slug the person's own press registered is stamped on every card, so the next press reopens on
 *         it (a card that already carries a slug keeps its own).
 *   - `withBrowserSignInPhase(html, phase)` writes the same state into a card's html, for a card drawn from
 *     the connection's record on reload (connectTileRehydrate.ts) and for the contrast fixtures.
 *
 * What a painted pill carries: its words for the phase (browserSignInPhaseCopy.ts, in the card's own
 * `data-lang`), `data-signin-phase`, `aria-busy` while the window is on its way, `aria-disabled` once signed
 * in (the press then does nothing: useGlobalTools.ts), and the card's own words in `data-signin-label`. It
 * NEVER sets `disabled`: under R1 the button stays usable until the sign-in is confirmed.
 *
 * Only the browser-lane card's own pill is painted, and (slice E, one flow on every surface) the run card's
 * "sign in to <app>" link, which reopens the same sign-in (desktopRunCardHtml.ts; the chat ignores a press on
 * it once it reads signed in). The legal apps card's buttons share the pill's id family, but React draws
 * them from the connection's facts (useBrowserSignInPhase), and a hand on a React-drawn node would fight React.
 *
 * Lever: REACT_APP_SIGNIN_CARD_LIVE=off (deploy.yml) restores the card as it was: the press greys it with
 * "opened ✓", nothing is painted, and a reload draws the card's own words.
 */
import { buildEnv } from '../../config/buildEnv';
import {
  BROWSER_SIGNIN_WINDOW_PHASES,
  allBrowserSignInPhases,
  browserSignInKey,
  getBrowserSignInPhase,
  subscribeBrowserSignInPhase,
  type BrowserSignInPhase,
  type BrowserSignInPhaseState,
} from './browserSignInPhase';
import { browserSignInButtonWords, type BrowserSignInLang } from './browserSignInPhaseCopy';

/** R1's live card is on unless the build says `off`. */
export function signInCardLive(env: Record<string, string | undefined> = buildEnv()): boolean {
  return String(env.REACT_APP_SIGNIN_CARD_LIVE ?? '').trim().toLowerCase() !== 'off';
}

/** The browser-lane card's pill: `${CONNECT_APP_BUTTON_IDS.browser}-open` (pinned equal by the card's test). */
export const SIGN_IN_PILL_ID = 'global-tool-connect-app-browser-btn-open';
const PILL_SELECTOR = `button[id="${SIGN_IN_PILL_ID}"][data-app-name]`;
/** The run card's "sign in to <app>" link (desktopRunCardHtml.ts): a signed-out run's own sign-in (slice E). */
export const RUN_CARD_SIGN_IN_SELECTOR = 'a[data-desktop-session][data-signin-slug][data-desktop-app]';
const PAINTED_SELECTOR = `${PILL_SELECTOR}, ${RUN_CARD_SIGN_IN_SELECTOR}`;

/** The app a painted element is for: the pill's `data-app-name`, the run card link's `data-desktop-app`. */
function appNameOf(el: Element): string {
  return el.getAttribute('data-app-name') || el.getAttribute('data-desktop-app') || '';
}

function isPaintable(el: Element): boolean {
  if (el.getAttribute('id') === SIGN_IN_PILL_ID) return Boolean(el.getAttribute('data-app-name'));
  return typeof el.matches === 'function' && el.matches(RUN_CARD_SIGN_IN_SELECTOR);
}

/** The custom-connection slug shape (connectApp.ts CUSTOM_CONNECTION_SLUG_RE, the same literal). */
const SLUG_RE = /^custom-[a-z0-9_]{1,50}$/;

function langOf(value: string | null | undefined): BrowserSignInLang {
  return value === 'es' ? 'es' : 'en';
}

/** The attributes a pill carries for a phase, in the order both painters write them. */
function phaseAttributes(phase: BrowserSignInPhase): Array<[string, string]> {
  if (phase === 'not-started') return [];
  const attrs: Array<[string, string]> = [['data-signin-phase', phase]];
  if (phase === 'opening') attrs.push(['aria-busy', 'true']);
  if (phase === 'signed-in') attrs.push(['aria-disabled', 'true']);
  return attrs;
}

/** Every attribute a phase may set, so a later phase can clear what an earlier one set. */
const PHASE_ATTRIBUTE_NAMES = ['data-signin-phase', 'aria-busy', 'aria-disabled'];

/**
 * Paint ONE pill (a live DOM button) for a phase. The card's own words are kept the first time, and a
 * well-formed slug is stamped when the pill has none. Returns false for anything that is not the pill.
 */
export function paintSignInPill(
  el: Element | null | undefined,
  phase: BrowserSignInPhase,
  opts: { slug?: string } = {},
): boolean {
  if (!el || typeof el.getAttribute !== 'function' || !isPaintable(el)) return false;
  const app = appNameOf(el);
  if (!app) return false;
  if (el.getAttribute('data-signin-label') === null) el.setAttribute('data-signin-label', el.textContent || '');
  const own = el.getAttribute('data-signin-label') || '';
  for (const name of PHASE_ATTRIBUTE_NAMES) el.removeAttribute(name);
  for (const [name, value] of phaseAttributes(phase)) el.setAttribute(name, value);
  el.textContent = phase === 'not-started' ? own : browserSignInButtonWords(phase, langOf(el.getAttribute('data-lang')), app);
  stampSlug(el, opts.slug);
  return true;
}

/** Stamp the slug the person's own press registered on a pill that carries none. */
function stampSlug(el: Element, slug: string | undefined): void {
  // The run card's link already names its own record (data-signin-slug); it takes no other.
  if (el.getAttribute('id') !== SIGN_IN_PILL_ID) return;
  const given = String(slug || '').trim();
  if (SLUG_RE.test(given) && !el.getAttribute('data-custom-slug')) el.setAttribute('data-custom-slug', given);
}

/** The message a pill's card belongs to ('' outside a message row). */
function messageIdOf(el: Element): string {
  const row = typeof el.closest === 'function' ? el.closest('[data-message-id]') : null;
  return (row && row.getAttribute('data-message-id')) || '';
}

/** Every pill for one app under `root` (the pill itself included). */
function pillsFor(root: ParentNode | Element, appKey: string | null): Element[] {
  const found: Element[] = [];
  const asElement = root as Element;
  if (typeof asElement.matches === 'function' && asElement.matches(PAINTED_SELECTOR)) found.push(asElement);
  if (typeof root.querySelectorAll === 'function') root.querySelectorAll(PAINTED_SELECTOR).forEach((el) => found.push(el));
  return appKey === null
    ? found
    : found.filter((el) => browserSignInKey(appNameOf(el)) === appKey);
}

/** Paint every pill for one app under `root` with its state. Returns how many were painted. */
export function paintSignInPillsFor(root: ParentNode | null | undefined, state: BrowserSignInPhaseState): number {
  if (!root || typeof root.querySelectorAll !== 'function') return 0;
  let painted = 0;
  for (const el of pillsFor(root, state.appKey)) {
    if (state.phase === 'not-started') {
      stampSlug(el, state.slug);
      continue;
    }
    if (paintSignInPill(el, state.phase, { slug: state.slug })) painted += 1;
  }
  return painted;
}

function isWindowPhase(phase: BrowserSignInPhase): boolean {
  return (BROWSER_SIGNIN_WINDOW_PHASES as readonly string[]).includes(phase);
}

/**
 * Paint the cards from the phase store, from now on (the host mounts it once). Returns the way to stop.
 * Never throws: a page with no document, or one whose DOM cannot be read, simply paints nothing.
 */
export function installBrowserSignInCards(doc: Document | null | undefined = typeof document !== 'undefined' ? document : null): () => void {
  if (!doc || !doc.body) return () => undefined;
  // The messages whose cards were on the page when their app's sign-in was settled ("signed in"), per app.
  const settled = new Map<string, Set<string>>();
  let observer: MutationObserver | null = null;

  const onChange = (state: BrowserSignInPhaseState) => {
    try {
      paintSignInPillsFor(doc, state);
      if (state.phase === 'signed-in' || state.phase === 'signed-out') {
        const ids = settled.get(state.appKey) ?? new Set<string>();
        for (const el of pillsFor(doc, state.appKey)) {
          const id = messageIdOf(el);
          if (id) ids.add(id);
        }
        settled.set(state.appKey, ids);
      }
    } catch {
      // A card that cannot be painted keeps its own words; the sign-in itself is untouched.
    }
    // The watch for cards drawn later starts with the first report: a page where nobody pressed a sign-in card
    // pays nothing.
    if (!observer) {
      try {
        watchDrawnCards();
      } catch {
        observer = null;
      }
    }
  };

  // A card drawn after the change: every card in a window phase; a settled card again; the slug on any.
  const paintDrawn = (pill: Element) => {
    const state = getBrowserSignInPhase(appNameOf(pill));
    if (!state) return;
    if (isWindowPhase(state.phase) || (state.phase !== 'not-started' && settled.get(state.appKey)?.has(messageIdOf(pill)))) {
      paintSignInPill(pill, state.phase, { slug: state.slug });
    } else {
      stampSlug(pill, state.slug);
    }
  };
  function watchDrawnCards(): void {
    if (typeof MutationObserver !== 'function' || !doc || !doc.body) return;
    observer = new MutationObserver((records) => {
      try {
        for (const record of records) {
          record.addedNodes.forEach((node) => {
            if (!node || node.nodeType !== 1) return;
            for (const pill of pillsFor(node as Element, null)) paintDrawn(pill);
          });
        }
      } catch {
        // As above: the card keeps its own words.
      }
    });
    observer.observe(doc.body, { childList: true, subtree: true });
  }

  const stop = subscribeBrowserSignInPhase(onChange);
  // Catch up with anything reported before the painter arrived.
  for (const state of allBrowserSignInPhases()) onChange(state);
  return () => {
    stop();
    observer?.disconnect();
    observer = null;
  };
}

/** What a press on a sign-in card does (useGlobalTools.ts's browser branch asks this, then acts). */
export interface SignInPress {
  /** The sign-in is confirmed: the press does nothing more. */
  ignore: boolean;
  /** The slug the window reopens on ('' = the flow registers the connection). */
  existingSlug: string;
  /** Tell every card the window is opening (false: a window already on its way, or open, is brought forward). */
  reportOpening: boolean;
  /** The lever off: the pressed card greys itself with "opened ✓", as before R1. */
  greyOut: boolean;
  /** Remember the slug the flow answers with: this press registered it. */
  rememberAnswer: boolean;
  /** A window that could not start leaves the sign-in not finished (only for a press that reported opening). */
  reportFailure: boolean;
}

/**
 * The press, decided from facts the click already holds: the lever, the phase painted on the pill, the slug the
 * card carries (and whether it is a "sign in again" card), the slug the person's own press registered earlier in
 * this page, and where the sign-in stands now.
 */
export function decideSignInPress(input: {
  live: boolean;
  phaseOnPill: string | null | undefined;
  givenSlug: string;
  reconnect: boolean;
  rememberedSlug: string;
  phaseNow: BrowserSignInPhase | null | undefined;
}): SignInPress {
  if (!input.live) {
    // The card as it was: only a reconnect pill hands its slug across (the flow re-validates it).
    return {
      ignore: false,
      existingSlug: input.reconnect ? input.givenSlug : '',
      reportOpening: false,
      greyOut: true,
      rememberAnswer: false,
      reportFailure: false,
    };
  }
  if (input.phaseOnPill === 'signed-in') {
    return { ignore: true, existingSlug: '', reportOpening: false, greyOut: false, rememberAnswer: false, reportFailure: false };
  }
  const given = SLUG_RE.test(input.givenSlug) ? input.givenSlug : '';
  const remembered = SLUG_RE.test(input.rememberedSlug) ? input.rememberedSlug : '';
  const existingSlug = given || remembered;
  const reportOpening = input.phaseNow !== 'opening' && input.phaseNow !== 'open';
  return { ignore: false, existingSlug, reportOpening, greyOut: false, rememberAnswer: !existingSlug, reportFailure: reportOpening };
}

function escapeHtml(text: string): string {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

function unescapeHtml(text: string): string {
  return String(text)
    .replace(/&#x27;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&');
}

const PILL_HTML_RE = new RegExp(`<button id="${SIGN_IN_PILL_ID}"([^>]*)>([^<]*)</button>`);

/**
 * The same state written into a card's html (a card drawn from its record, a fixture): the pill's words for
 * the phase and the attributes `paintSignInPill` sets. 'not-started' and html with no pill come back as they
 * were.
 */
export function withBrowserSignInPhase(cardHtml: string, phase: BrowserSignInPhase): string {
  const html = String(cardHtml || '');
  if (phase === 'not-started') return html;
  const m = PILL_HTML_RE.exec(html);
  if (!m) return html;
  const attrs = m[1];
  if (/\sdata-signin-phase=/.test(attrs)) return html;
  const app = unescapeHtml((/\sdata-app-name="([^"]*)"/.exec(attrs) || [])[1] || '');
  if (!app) return html;
  const lang = langOf((/\sdata-lang="([^"]*)"/.exec(attrs) || [])[1]);
  const own = m[2];
  const added = [['data-signin-label', unescapeHtml(own)] as [string, string], ...phaseAttributes(phase)]
    .map(([name, value]) => ` ${name}="${escapeHtml(value)}"`)
    .join('');
  const pill = `<button id="${SIGN_IN_PILL_ID}"${attrs}${added}>${escapeHtml(browserSignInButtonWords(phase, lang, app))}</button>`;
  return html.slice(0, m.index) + pill + html.slice(m.index + m[0].length);
}
